import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../../../components/feature/Navbar";
import { supabase } from "../../../lib/supabase";
import { useApp } from "../../../contexts/AppContext";

type ErrorType = "invalid_credentials" | "email_not_confirmed" | "rate_limit" | "network" | "unknown";

function getErrorInfo(msg: string): { type: ErrorType; text: string } {
  if (msg.includes("Email not confirmed") || msg.includes("email_not_confirmed")) {
    return { type: "email_not_confirmed", text: "メールアドレスの確認が完了していません。登録時に届いた確認メールをご確認ください。" };
  }
  if (msg.includes("Invalid login credentials") || msg.includes("invalid_credentials") || msg.includes("Invalid email or password")) {
    return { type: "invalid_credentials", text: "メールアドレスまたはパスワードが正しくありません。" };
  }
  if (msg.includes("rate") || msg.includes("too many")) {
    return { type: "rate_limit", text: "ログイン試行回数が多すぎます。しばらく待ってから再度お試しください。" };
  }
  if (msg.includes("fetch") || msg.includes("network")) {
    return { type: "network", text: "ネットワークエラーが発生しました。接続を確認して再試行してください。" };
  }
  return { type: "unknown", text: "ログインに失敗しました。しばらく時間をおいて再試行してください。" };
}

export default function LoginPage() {
  const [loginMode, setLoginMode] = useState<"consumer" | "provider">("consumer");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<{ type: ErrorType; text: string } | null>(null);
  const [resetMode, setResetMode] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetSent, setResetSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendDone, setResendDone] = useState(false);
  const { setMode } = useApp();
  const navigate = useNavigate();

  useEffect(() => { setTimeout(() => setVisible(true), 80); }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;
    setError(null);
    setLoading(true);

    const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });

    if (signInError) {
      setError(getErrorInfo(signInError.message));
      setLoading(false);
      return;
    }

    setMode(loginMode);
    setLoading(false);
    navigate(loginMode === "provider" ? "/mypage/provider" : "/mypage");
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetEmail) return;
    setResetLoading(true);
    await supabase.auth.resetPasswordForEmail(resetEmail, {
      redirectTo: `${window.location.origin}/login`,
    });
    setResetSent(true);
    setResetLoading(false);
  };

  const handleResendConfirmation = async () => {
    setResendLoading(true);
    await supabase.auth.resend({ type: "signup", email });
    setResendDone(true);
    setResendLoading(false);
  };

  return (
    <div className={`min-h-screen bg-ivory flex flex-col transition-opacity duration-700 ${visible ? "opacity-100" : "opacity-0"}`}>
      <Navbar />
      <main className="flex-1 flex items-center justify-center px-4 py-20 pt-28">
        <div className="w-full max-w-md">
          {/* パスワードリセットモード */}
          {resetMode ? (
            <>
              <div className="text-center mb-8">
                <p className="font-display italic text-gold text-xs tracking-widest mb-2">Reset Password</p>
                <h1 className="font-serif text-deep-green text-3xl font-light">パスワードをリセット</h1>
                <p className="text-sm text-charcoal/50 font-sans mt-2">登録済みのメールアドレスを入力してください</p>
              </div>
              <div className="bg-white rounded-2xl p-8 border border-stone-100">
                {resetSent ? (
                  <div className="text-center py-4">
                    <div className="w-14 h-14 flex items-center justify-center rounded-full bg-teal/10 mx-auto mb-4">
                      <i className="ri-mail-check-line text-2xl text-teal" />
                    </div>
                    <p className="font-sans text-sm text-charcoal font-medium mb-2">メールを送信しました</p>
                    <p className="text-xs text-charcoal/50 font-sans leading-relaxed">
                      パスワードリセット用のリンクを送信しました。メールをご確認ください。
                    </p>
                    <button onClick={() => { setResetMode(false); setResetSent(false); setResetEmail(""); }} className="mt-5 text-sm text-teal font-sans cursor-pointer hover:underline whitespace-nowrap">
                      ← ログインに戻る
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handlePasswordReset} className="flex flex-col gap-5">
                    <div>
                      <label className="text-xs text-charcoal/50 font-sans mb-1.5 block">メールアドレス</label>
                      <div className="relative">
                        <i className="ri-mail-line absolute left-3.5 top-1/2 -translate-y-1/2 text-charcoal/30 text-sm" />
                        <input
                          type="email"
                          required
                          value={resetEmail}
                          onChange={(e) => setResetEmail(e.target.value)}
                          placeholder="example@email.com"
                          className="w-full pl-10 pr-4 py-3 rounded-xl border border-stone-200 text-sm font-sans text-charcoal placeholder-charcoal/30 focus:outline-none focus:border-teal transition-colors bg-ivory"
                        />
                      </div>
                    </div>
                    <button
                      type="submit"
                      disabled={resetLoading || !resetEmail}
                      className="w-full py-4 rounded-full text-sm font-sans font-medium cursor-pointer transition-all duration-300 btn-primary disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      {resetLoading ? (
                        <span className="flex items-center justify-center gap-2">
                          <i className="ri-loader-4-line animate-spin" />送信中...
                        </span>
                      ) : "リセットメールを送る"}
                    </button>
                    <button type="button" onClick={() => setResetMode(false)} className="text-center text-sm text-charcoal/50 font-sans cursor-pointer hover:text-charcoal transition-colors whitespace-nowrap">
                      ← ログインに戻る
                    </button>
                  </form>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="text-center mb-8">
                <p className="font-display italic text-gold text-xs tracking-widest mb-2">Welcome back</p>
                <h1 className="font-serif text-deep-green text-3xl font-light">ログイン</h1>
              </div>

              <div className="flex bg-stone-100 rounded-full p-1 mb-8">
                {(["consumer", "provider"] as const).map((m) => (
                  <button
                    key={m}
                    onClick={() => setLoginMode(m)}
                    className={`flex-1 py-2.5 rounded-full text-sm font-sans font-medium cursor-pointer transition-all duration-300 whitespace-nowrap ${loginMode === m ? (m === "consumer" ? "bg-white text-deep-green" : "bg-provider-bg text-white") : "text-charcoal/50 hover:text-charcoal"}`}
                  >
                    {m === "consumer" ? (
                      <span className="flex items-center justify-center gap-1.5"><i className="ri-user-line" />受講者</span>
                    ) : (
                      <span className="flex items-center justify-center gap-1.5"><i className="ri-store-2-line" />事業者</span>
                    )}
                  </button>
                ))}
              </div>

              <div className={`bg-white rounded-2xl p-8 border transition-all duration-500 ${loginMode === "provider" ? "border-deep-green/30" : "border-stone-100"}`}>
                <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                  <div>
                    <label className="text-xs text-charcoal/50 font-sans mb-1.5 block">メールアドレス</label>
                    <div className="relative">
                      <i className="ri-mail-line absolute left-3.5 top-1/2 -translate-y-1/2 text-charcoal/30 text-sm" />
                      <input
                        type="email"
                        required
                        value={email}
                        onChange={(e) => { setEmail(e.target.value); setError(null); }}
                        placeholder="example@email.com"
                        className="w-full pl-10 pr-4 py-3 rounded-xl border border-stone-200 text-sm font-sans text-charcoal placeholder-charcoal/30 focus:outline-none focus:border-teal transition-colors bg-ivory"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-charcoal/50 font-sans mb-1.5 block">パスワード</label>
                    <div className="relative">
                      <i className="ri-lock-line absolute left-3.5 top-1/2 -translate-y-1/2 text-charcoal/30 text-sm" />
                      <input
                        type={showPass ? "text" : "password"}
                        required
                        value={password}
                        onChange={(e) => { setPassword(e.target.value); setError(null); }}
                        placeholder="パスワードを入力"
                        className="w-full pl-10 pr-10 py-3 rounded-xl border border-stone-200 text-sm font-sans text-charcoal placeholder-charcoal/30 focus:outline-none focus:border-teal transition-colors bg-ivory"
                      />
                      <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-charcoal/30 hover:text-charcoal cursor-pointer">
                        <i className={showPass ? "ri-eye-off-line" : "ri-eye-line"} />
                      </button>
                    </div>
                    <div className="flex justify-end mt-1.5">
                      <button
                        type="button"
                        onClick={() => { setResetMode(true); setResetEmail(email); }}
                        className="text-xs text-charcoal/40 hover:text-teal font-sans cursor-pointer transition-colors whitespace-nowrap"
                      >
                        パスワードをお忘れの方
                      </button>
                    </div>
                  </div>

                  {error && (
                    <div className={`text-xs font-sans rounded-xl px-4 py-3 space-y-2 ${error.type === "rate_limit" ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-500"}`}>
                      <p className="flex items-start gap-1.5">
                        <i className={`${error.type === "rate_limit" ? "ri-time-line" : "ri-error-warning-line"} flex-shrink-0 mt-0.5`} />
                        {error.text}
                      </p>
                      {error.type === "email_not_confirmed" && (
                        <button
                          type="button"
                          onClick={handleResendConfirmation}
                          disabled={resendLoading || resendDone}
                          className="flex items-center gap-1.5 text-teal hover:underline cursor-pointer disabled:opacity-50 whitespace-nowrap"
                        >
                          {resendDone ? (
                            <><i className="ri-checkbox-circle-line" />確認メールを再送しました</>
                          ) : resendLoading ? (
                            <><i className="ri-loader-4-line animate-spin" />送信中...</>
                          ) : (
                            <><i className="ri-send-plane-line" />確認メールを再送する</>
                          )}
                        </button>
                      )}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading || !email || !password}
                    className={`w-full py-4 rounded-full text-sm font-sans font-medium cursor-pointer transition-all duration-300 whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed ${loginMode === "provider" ? "bg-provider-bg text-white hover:opacity-90" : "btn-primary"}`}
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin" />ログイン中...
                      </span>
                    ) : "ログインする →"}
                  </button>
                </form>
              </div>

              <p className="text-center text-sm font-sans text-charcoal/50 mt-6">
                アカウントをお持ちでない方は
                <Link to="/register" className="text-teal hover:text-deep-green ml-1 cursor-pointer">新規登録 →</Link>
              </p>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
